import {
  useState,
  useMemo,
  type HTMLAttributes,
  type ImgHTMLAttributes,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const avatarSizeMap = {
  xs: { container: 'h-6 w-6', text: 'text-[10px]', status: 'h-1.5 w-1.5 border' },
  sm: { container: 'h-8 w-8', text: 'text-xs', status: 'h-2 w-2 border-[1.5px]' },
  md: { container: 'h-10 w-10', text: 'text-sm', status: 'h-2.5 w-2.5 border-[1.5px]' },
  lg: { container: 'h-12 w-12', text: 'text-base', status: 'h-3 w-3 border-2' },
  xl: { container: 'h-16 w-16', text: 'text-lg', status: 'h-3.5 w-3.5 border-2' },
};

const avatarVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center',
    'overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800',
  ],
  {
    variants: {
      ring: {
        true: 'ring-2 ring-white dark:ring-slate-900',
        false: '',
      },
    },
    defaultVariants: {
      ring: false,
    },
  }
);

const statusColorMap = {
  online: 'bg-emerald-500',
  offline: 'bg-slate-400',
  busy: 'bg-rose-500',
};

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarStatus = 'online' | 'offline' | 'busy';

export interface AvatarProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  /** Image source URL */
  src?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** Name used to generate initials as fallback */
  name?: string;
  /** Size of the avatar */
  size?: AvatarSize;
  /** Status indicator dot */
  status?: AvatarStatus;
  /** Additional img element props */
  imgProps?: ImgHTMLAttributes<HTMLImageElement>;
}

/**
 * Extract initials from a name string (max 2 characters).
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Generate a consistent background color from a name string.
 */
function getColorFromName(name: string): string {
  const colors = [
    'bg-indigo-500',
    'bg-sky-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-violet-500',
    'bg-cyan-500',
    'bg-pink-500',
    'bg-teal-500',
    'bg-orange-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function Avatar({
  className,
  src,
  alt,
  name,
  size = 'md',
  status,
  ring,
  imgProps,
  ...props
}: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const sizes = avatarSizeMap[size];
  const showImage = src && !imgError;

  const initials = useMemo(
    () => (name ? getInitials(name) : '?'),
    [name]
  );

  const bgColor = useMemo(
    () => (name ? getColorFromName(name) : 'bg-slate-400'),
    [name]
  );

  return (
    <div
      className={cn(
        avatarVariants({ ring }),
        sizes.container,
        className
      )}
      role="img"
      aria-label={alt ?? name ?? 'Avatar'}
      {...props}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt ?? name ?? 'Avatar'}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
          draggable={false}
          {...imgProps}
        />
      ) : (
        <div
          className={cn(
            'flex h-full w-full items-center justify-center font-medium text-white',
            bgColor
          )}
        >
          <span className={sizes.text}>{initials}</span>
        </div>
      )}
      {status && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-white dark:border-slate-900',
            sizes.status,
            statusColorMap[status]
          )}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  );
}

Avatar.displayName = 'Avatar';

export { Avatar, avatarVariants };
